import React, { useState } from "react";
import api, { buildEmbeds, directUrl, BACKEND_URL } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ShareBar from "@/components/ShareBar";
import QRModal from "@/components/QRModal";
import { Check, Copy, QrCode, ExternalLink, Loader2 } from "lucide-react";

const TABS = [
  { key: "direct", label: "Direct link", testid: "direct" },
  { key: "html", label: "HTML", testid: "html" },
  { key: "bbcode", label: "BBCode", testid: "bbcode" },
  { key: "markdown", label: "Markdown", testid: "markdown" },
  { key: "short", label: "Short link", testid: "short" },
];

const FORMATS = [
  { key: "original", label: "Original" },
  { key: "png", label: "PNG" },
  { key: "jpg", label: "JPG" },
  { key: "webp", label: "WEBP" },
];

function CodeBox({ code, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative">
      <pre
        data-testid={`code-${testid}`}
        className="font-mono text-xs sm:text-sm bg-secondary/60 border border-border rounded-xl p-4 pr-14 overflow-x-auto whitespace-pre-wrap break-all min-h-[52px]"
      >
        {code}
      </pre>
      <button
        onClick={copy}
        data-testid={`copy-${testid}-btn`}
        className={`absolute top-2.5 right-2.5 h-9 w-9 rounded-lg flex items-center justify-center transition-all ${
          copied ? "bg-accent text-accent-foreground" : "bg-background border border-border hover:bg-secondary"
        }`}
        title="Copy"
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function GeneratedLinks({ image }) {
  const [qrOpen, setQrOpen] = useState(false);
  const [fmt, setFmt] = useState("original");
  const [fmtUrl, setFmtUrl] = useState(null);
  const [converting, setConverting] = useState(null);
  if (!image) return null;

  const isConvertible = image.source_type === "upload" || image.source_type === "url";
  const activeUrl = fmt === "original" ? directUrl(image) : fmtUrl || directUrl(image);
  const embeds = buildEmbeds(image, fmt === "original" ? undefined : fmtUrl);
  const preview = activeUrl;
  const shareUrl = embeds.short;

  const selectFormat = async (key) => {
    if (key === fmt && key === "original") return;
    if (key === "original") {
      setFmt("original");
      setFmtUrl(null);
      return;
    }
    setConverting(key);
    try {
      const res = await api.get(`/images/${image.short_code}/convert`, { params: { format: key } });
      setFmtUrl(`${BACKEND_URL}/api/file/${res.data.storage_path}`);
      setFmt(key);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed");
    } finally {
      setConverting(null);
    }
  };

  return (
    <div className="animate-fade-up rounded-2xl border border-border bg-card overflow-hidden" data-testid="generated-links-panel">
      <div className="grid md:grid-cols-5 gap-0">
        {/* preview */}
        <div className="md:col-span-2 checker-bg flex items-center justify-center p-6 border-b md:border-b-0 md:border-r border-border min-h-[260px]">
          <img
            src={preview}
            alt={image.title}
            data-testid="result-preview-img"
            className="max-h-[320px] max-w-full rounded-lg shadow-sm object-contain"
          />
        </div>

        {/* codes */}
        <div className="md:col-span-3 p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h3 className="font-heading font-bold text-lg tracking-tight">Your links are ready</h3>
              <p className="text-sm text-muted-foreground mt-0.5 truncate max-w-xs">{image.title}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={() => setQrOpen(true)} data-testid="open-qr-btn" title="QR code">
                <QrCode className="h-4 w-4" />
              </Button>
              <a href={preview} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="icon" data-testid="open-direct-btn" title="Open image">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>

          {isConvertible && (
            <div className="mb-4" data-testid="format-bar">
              <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase mb-2">Format</p>
              <div className="flex flex-wrap gap-1.5">
                {FORMATS.map((f) => {
                  const active = fmt === f.key;
                  const loading = converting === f.key;
                  return (
                    <button
                      key={f.key}
                      onClick={() => selectFormat(f.key)}
                      disabled={!!converting}
                      data-testid={`format-${f.key}`}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors disabled:opacity-60 ${
                        active
                          ? "bg-accent text-accent-foreground border-accent"
                          : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
                      }`}
                    >
                      {loading && <Loader2 className="h-3 w-3 animate-spin" />}
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <Tabs defaultValue="direct">
            <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 mb-4">
              {TABS.map((t) => (
                <TabsTrigger
                  key={t.key}
                  value={t.key}
                  data-testid={`linktab-${t.testid}`}
                  className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-full px-3.5 py-1.5 text-xs font-medium border border-border"
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {TABS.map((t) => (
              <TabsContent key={t.key} value={t.key} className="mt-0">
                <CodeBox code={embeds[t.key]} testid={t.testid} />
              </TabsContent>
            ))}
          </Tabs>

          <div className="mt-6 pt-5 border-t border-border">
            <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase mb-3">Share</p>
            <ShareBar shareUrl={shareUrl} title={image.title} />
          </div>
        </div>
      </div>
      <QRModal open={qrOpen} onOpenChange={setQrOpen} value={preview} />
    </div>
  );
}
